// react
import { createSlice, PayloadAction, isAnyOf } from "@reduxjs/toolkit";
// redux
import { useAppSelector, createAppAsyncThunk } from "../../redux/hooks";
import { startAppListening } from "../../redux/listenerMiddleware";
// api
import { createBasketClient } from "../../api/clientFactory";
import * as Client from "../../api/Client";
import { call } from "../../api/callHelper";
// common
import { logError } from "../../common/LogHelper";

// state
export interface INopimsBasketModel {
    readonly userToken: string | undefined;
    readonly items: INopimsBasketItemModel[];
}

export interface INopimsBasketItemModel {
    readonly dataDescription: string;
    readonly reportId: number | undefined;
    readonly surveyDataId: number | undefined;
    readonly wellBoreholeId: number | undefined;
    readonly surveyAcquisitionId: number | undefined;
    readonly reprocessingProjectId: number | undefined;
    readonly itemId: string | undefined;
    readonly activity: string | undefined;
    readonly activityIdentifier: string | undefined;
    readonly activityName: string | undefined;
    readonly type: string | undefined;
    readonly documentType: string | undefined;
    readonly infoUrl: string | undefined;
    readonly infoName: string | undefined;
    readonly isInfoDownloaded: boolean | undefined;
    readonly downloadName: string | undefined;
    readonly downloadUrl: string | undefined;
    readonly fileSizeBytes: number | undefined;
    readonly isFileDownloaded: boolean | undefined;
    readonly isChecked: boolean | undefined;
}

// initial state
const initialState: INopimsBasketModel = {
    userToken: undefined,
    items: [],
};

// reducer
export const nopimsBasketSlice = createSlice({
    name: "nopimsBasket",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<string | undefined>) => {
            state.userToken = action.payload;
        },
        itemsAdded: (state, action: PayloadAction<INopimsBasketItemModel[]>) => {
            // ensure added items don't match existing items on keys (report id, survey id)
            // Get the distinct items passed in
            const distinctItems = action.payload.reduce(function (itemList: INopimsBasketItemModel[], i: INopimsBasketItemModel) {
                const matches = itemList.filter(function (i2) {
                    return (i2.reportId && i2.reportId === i.reportId) || (i2.surveyDataId && i2.surveyDataId === i.surveyDataId);
                });
                if (matches.length === 0) itemList.push(i);
                return itemList;
            }, []);

            state.items = state.items.concat(
                distinctItems.filter((i) => !state.items.some((i2) => (i2.reportId && i2.reportId === i.reportId) || (i2.surveyDataId && i2.surveyDataId === i.surveyDataId)))
            );
        },
        itemInfoDownloaded: (state, action: PayloadAction<INopimsBasketItemModel>) => {
            // items downloaded will only ever shift to true
            state.items = state.items.map((i) => {
                if ((action.payload.reportId && action.payload.reportId === i.reportId) || (action.payload.surveyDataId && action.payload.surveyDataId === i.surveyDataId)) {
                    return { ...i, isInfoDownloaded: true };
                }
                return i;
            });
        },
        itemFileDownloaded: (state, action: PayloadAction<INopimsBasketItemModel>) => {
            // items downloaded will only ever shift to true
            state.items = state.items.map((i) => {
                if ((action.payload.reportId && action.payload.reportId === i.reportId) || (action.payload.surveyDataId && action.payload.surveyDataId === i.surveyDataId)) {
                    return { ...i, isFileDownloaded: true };
                }
                return i;
            });
        },
        batchItemsDownloaded: (state) => {
            // items downloaded will only ever shift to true
            state.items = state.items.map((i) => {
                if (i.isChecked) {
                    return { ...i, isChecked: false, isFileDownloaded: true };
                }
                return i;
            });
        },
        itemsRemoved: (state, action: PayloadAction<INopimsBasketItemModel[]>) => {
            // items can only be removed if they were actually in the list
            state.items = state.items.filter((i) => !action.payload.some((i2) => (i2.reportId && i2.reportId === i.reportId) || (i2.surveyDataId && i2.surveyDataId === i.surveyDataId)));
        },
        itemsChecked: (state, action: PayloadAction<INopimsBasketItemModel[]>) => {
            // items downloaded will only ever shift to true
            state.items = state.items.map((i) => {
                if (action.payload.some((i2) => (i2.reportId && i2.reportId === i.reportId) || (i2.surveyDataId && i2.surveyDataId === i.surveyDataId))) {
                    return { ...i, isChecked: true };
                }
                return i;
            });
        },
        itemsUnChecked: (state, action: PayloadAction<INopimsBasketItemModel[]>) => {
            // items downloaded will only ever shift to true
            state.items = state.items.map((i) => {
                if (action.payload.some((i2) => (i2.reportId && i2.reportId === i.reportId) || (i2.surveyDataId && i2.surveyDataId === i.surveyDataId))) {
                    return { ...i, isChecked: false };
                }
                return i;
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadItems.fulfilled, (state, action) => {
            if (action.payload) {
                state.items = action.payload;
            }
        });
    },
    selectors: {
        selectNopimsBasket: (state) => state,
    },
});

// actions
export const { setUser, itemsAdded, itemInfoDownloaded, itemFileDownloaded, batchItemsDownloaded, itemsRemoved, itemsChecked, itemsUnChecked } = nopimsBasketSlice.actions;
// selectors
export const { selectNopimsBasket } = nopimsBasketSlice.selectors;
// use selector
export function useNopimsBasket() {
    return useAppSelector(selectNopimsBasket);
}

// TODO need to decide what we want to retry and log here
// previously we were using simpleCall which retried, logged to appInsights and handled API error toast notifications
// now there is only call which retries, logs to appInsights and returns a CallResultError
// - do we need to log anything here, and do we need to retry these calls?

// thunks

// load the active basket from the service layer (there can only be 0 or 1)
// do not show busy state as this needs to be done silently
export const loadItems = createAppAsyncThunk("nopimsBasket/loadItems", async (_, { getState }) => {
    const { nopimsBasket } = getState();

    const client = createBasketClient();
    const request = new Client.GetPublicNopimsBasketRequestDto({ userToken: nopimsBasket.userToken! });
    const result = await call(() => client.getPublicNopimsBasket(request));

    if (result.Error) {
        logError(`nopimsBasket/loadItems failed with correlation '${result.Error.unitOfWorkCorrelationId}'`);
        return;
    }

    const items = result.Response!.items.map((i) =>
        getBasketItemModel(
            i.dataDescription,
            i.reportId,
            i.surveyDataId,
            i.wellBoreholeId,
            i.surveyAcquisitionId,
            i.reprocessingProjectId,
            i.itemId,
            i.activity,
            i.activityIdentifier,
            i.activityName,
            i.type,
            i.documentType,
            i.infoUrl,
            i.infoName,
            i.isInfoDownloaded,
            i.downloadName,
            i.downloadUrl,
            i.fileSizeBytes,
            i.isFileDownloaded,
            false
        )
    );

    return items;
});

//  update to the service layer
// do not show busy state as this needs to be done silently
// refresh the vm after the save has occurred to help reduce concurrency issues
// will ignore conflicts for now, just log these, will see how this goes
startAppListening({
    matcher: isAnyOf(itemsAdded, itemInfoDownloaded, itemFileDownloaded, batchItemsDownloaded, itemsRemoved),
    effect: async (action, { getState, dispatch }) => {
        const { nopimsBasket } = getState();

        const client = createBasketClient();
        const saveRequest = new Client.SavePublicNopimsBasketRequestDto({
            userToken: nopimsBasket.userToken!,
            items: nopimsBasket.items.map(
                (i) =>
                    new Client.SavePublicNopimsBasketItemRequestDto({
                        reportId: i.reportId,
                        surveyDataId: i.surveyDataId,
                        wellBoreholeId: i.wellBoreholeId,
                        surveyAcquisitionId: i.surveyAcquisitionId,
                        reprocessingProjectId: i.reprocessingProjectId,
                        isInfoDownloaded: i.isInfoDownloaded!,
                        isFileDownloaded: i.isFileDownloaded!,
                    })
            ),
        });
        const result = await call(() => client.savePublicNopimsBasket(saveRequest));

        if (result.Error) {
            logError(`${action.type} failed with correlation '${result.Error.unitOfWorkCorrelationId}'.`);
        } else {
            dispatch(loadItems());
        }
    },
});

export default nopimsBasketSlice.reducer;

export function getBasketItemModel(
    dataDescription: string,
    reportId: number | undefined,
    surveyDataId: number | undefined,
    wellBoreholeId: number | undefined,
    surveyAcquisitionId: number | undefined,
    reprocessingProjectId: number | undefined,
    itemId?: string | undefined,
    activity?: string | undefined,
    activityIdentifier?: string | undefined,
    activityName?: string | undefined,
    type?: string | undefined,
    documentType?: string | undefined,
    infoUrl?: string | undefined,
    infoName?: string | undefined,
    isInfoDownloaded?: boolean,
    downloadName?: string | undefined,
    downloadUrl?: string | undefined,
    fileSizeBytes?: number | undefined,
    isFileDownloaded?: boolean,
    isChecked?: boolean
): INopimsBasketItemModel {
    return {
        dataDescription: dataDescription && dataDescription !== "" ? dataDescription : itemId ?? "",
        reportId,
        surveyDataId,
        wellBoreholeId,
        surveyAcquisitionId,
        reprocessingProjectId,
        itemId,
        activity,
        activityIdentifier,
        activityName,
        type,
        documentType,
        infoUrl,
        infoName,
        isInfoDownloaded,
        downloadName,
        downloadUrl,
        fileSizeBytes,
        isFileDownloaded,
        isChecked,
    };
}
